/**
 * Colours stored by the legacy admin are not always valid (its default was "#00000"). Map styles and
 * the shape editor reject invalid ones, so normalise to #rrggbb with a fallback.
 */
export function safeHex(color: unknown, fallback = '#64748b'): `#${string}` {
  if (typeof color !== 'string') return fallback as `#${string}`;
  const c = color.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(c)) return c as `#${string}`;
  if (/^#[0-9a-f]{3}$/.test(c)) return `#${c[1]}${c[1]}${c[2]}${c[2]}${c[3]}${c[3]}`;
  if (/^#[0-9a-f]{8}$/.test(c)) return c.slice(0, 7) as `#${string}`;
  return fallback as `#${string}`;
}

/** Black or white, whichever reads better on `color`. */
export function textOn(color: string): '#ffffff' | '#0f172a' {
  const c = safeHex(color);
  const [r, g, b] = [1, 3, 5].map((i) => Number.parseInt(c.slice(i, i + 2), 16) / 255) as [
    number,
    number,
    number,
  ];
  const lin = (v: number) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return luminance > 0.45 ? '#0f172a' : '#ffffff';
}

/** A darker shade of `color` (for outlines). */
export function shade(color: string, amount = 0.35): string {
  const c = safeHex(color);
  const parts = [1, 3, 5].map((i) =>
    Math.round(Number.parseInt(c.slice(i, i + 2), 16) * (1 - amount))
      .toString(16)
      .padStart(2, '0'),
  );
  return `#${parts.join('')}`;
}
